'use client'

import Image from 'next/image'
import QRCode from 'react-qr-code'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { ABHA_ANALYTICS_EVENTS } from '@components/NewProduct/AbhaHeroWidget/abhaHeroAnalytics'
import {
  ABHA_HERO_SUCCESS_APP_LINK,
  ABHA_HERO_SUCCESS_QR_SUBTITLE,
  ABHA_HERO_SUCCESS_QR_TITLE,
} from '@components/NewProduct/constants'
import { getClassNames } from '@helpers/productWidgetHelper'
import type {
  AbhaSuccessPanelVariant,
  AbhaSuccessQrCardBlock,
} from '@components/NewProduct/AbhaHeroWidget/steps/abhaSuccessRightPanelTypes'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'

export type AbhaHeroSuccessQrCardProps = {
  block: AbhaSuccessQrCardBlock
  panelVariant: AbhaSuccessPanelVariant
  appDownloadLink: string
  isMobile?: boolean
  analyticsProps?: Record<string, unknown>
}

export const AbhaHeroSuccessQrCard = ({
  block,
  panelVariant,
  appDownloadLink,
  isMobile = false,
  analyticsProps,
}: AbhaHeroSuccessQrCardProps) => {
  const showQr = panelVariant === 'plain' ? true : block.hasQr !== false
  const qrValue = appDownloadLink || block.appLink || ABHA_HERO_SUCCESS_APP_LINK
  const hasCornerImage = Boolean(block.cornerImage)

  const handleCtaClick = () => {
    const eventName = analyticsProps ? ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET : block.ctaProps?.eventName

    if (eventName) {
      void trackEvent(eventName, {
        ...block.ctaProps?.eventProps,
        ...analyticsProps,
        ...(analyticsProps
          ? {
              page: window.location.href,
              path: window.location.pathname,
            }
          : {}),
      })
    }

    if (block.ctaProps?.redirectionLink) {
      window.open(block.ctaProps.redirectionLink, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <div
      className={getClassNames(
        [
          'abha-hero-success-panel__qr-card',
          isMobile ? 'abha-hero-success-panel__qr-card--mobile' : 'abha-hero-success-panel__qr-card--desktop',
          `abha-hero-success-panel__qr-card--${panelVariant}`,
          showQr ? 'abha-hero-success-panel__qr-card--has-qr' : 'abha-hero-success-panel__qr-card--no-qr',
        ],
        {
          'abha-hero-success-panel__qr-card--has-corner-image': hasCornerImage,
        }
      )}>
      <div className="abha-hero-success-panel__qr-copy">
        <div className="abha-hero-success-panel__qr-heading">
          <p className="abha-hero-success-panel__qr-title">{block.title ?? ABHA_HERO_SUCCESS_QR_TITLE}</p>
          <p className="abha-hero-success-panel__qr-subtitle">{block.subTitle ?? ABHA_HERO_SUCCESS_QR_SUBTITLE}</p>
        </div>

        {block?.image ? (
          <Image
            src={block?.image}
            alt=""
            width={154}
            height={45}
            className="abha-hero-success-panel__qr-brand-image"
            aria-hidden
          />
        ) : null}

        <PropertyControlledComponent controllerProperty={!!block?.ctaText} fallback={null}>
          <button type="button" className="abha-hero-success-panel__qr-cta" onClick={handleCtaClick}>
            {block?.ctaText}
          </button>
        </PropertyControlledComponent>
      </div>

      <PropertyControlledComponent controllerProperty={showQr} fallback={null}>
        <div className="abha-hero-success-panel__qr-code" aria-hidden>
          <QRCode className="abha-hero-success-panel__qr-image" value={qrValue} size={isMobile ? 96 : 125} />
        </div>
      </PropertyControlledComponent>

      {block?.cornerImage ? (
        <Image
          src={block?.cornerImage}
          alt=""
          width={isMobile ? 90 : 170}
          height={isMobile ? 90 : 170}
          className="abha-hero-success-panel__qr-corner-image"
          aria-hidden
        />
      ) : null}
    </div>
  )
}
